import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  FormHelperText,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import { isEmpty, get, uniqueId, isNil } from 'lodash';
import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { translate } from 'containers/services/translate';
import { COLORS } from 'constants/design';
import { getElementTitle } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import {
  OTHER_REJECTION_REASON,
  REJECTION_REGULATION_TYPE,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';

const StyledDiv = styled.div`
  width: 576px;
  display: flex;
  align-items: center;
  justify-content: center;

  .select {
    width: 100%;
    margin-bottom: 23px;
    margin-top: 10px;
    font-size: 13px;
  }
`;
const StyledDialogActions = styled(DialogActions)`
  display: flex;
  padding: 20px 30px;
  justify-content: space-between;

  .moveButton {
    border: 1px solid ${COLORS.YELLOW};
    background-color: ${COLORS.YELLOW};
    color: ${COLORS.LIMED_SPURCE};
    &:hover {
      background-color: ${COLORS.YELLOW};
    }
  }

  .Mui-disabled {
    opacity: 0.6;
  }
`;

export interface RegulationValidationDialogProps {
  intl: IntlShape;
  open: boolean;
  onClose: Function;
  handleValidation: any;
  isValidation: boolean;
  listings: any;
}

const RegulationValidationDialog: React.FC<RegulationValidationDialogProps> = (
  props,
): JSX.Element => {
  const inputLabel = React.useRef<HTMLLabelElement>(null);

  const [
    regulationValidationComment,
    setRegulationValidationComment,
  ] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [rejectionType, setRejectionType] = useState<string | null>(null);
  const { onClose, open } = props;

  const handleClose = () => {
    setRegulationValidationComment('');
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<{ value: string; name: string }>,
  ) => {
    setRegulationValidationComment(e.target.value);
  };

  const handleChangeReason = (e: React.ChangeEvent<{ value: unknown }>) => {
    setRegulationValidationComment(null);
    setReasonCode(e.target.value as any);
  };
  const handleChangeRejectionType = (
    e: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setRegulationValidationComment(null);
    setReasonCode(null);
    setRejectionType(e.target.value as any);
  };
  const disableButton =
    (!props.isValidation &&
      (!regulationValidationComment ||
        isNil(regulationValidationComment) ||
        isEmpty(regulationValidationComment)) &&
      getElementTitle(
        get(props.listings, 'regulationRejectionReasons', []),
        reasonCode,
      ) === OTHER_REJECTION_REASON) ||
    (!props.isValidation && (isNil(reasonCode) || isNil(rejectionType)));
  const textFieldLabel =
    props.isValidation ||
    (getElementTitle(
      get(props.listings, 'regulationRejectionReasons', []),
      reasonCode,
    ) !== OTHER_REJECTION_REASON &&
      featureService.hasFeature(FEATURES.EVOLUTION_P1))
      ? translate({ ...messages.RegulationValidationComment }, props)
      : translate({ ...messages.RegulationValidationReason }, props);

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <DialogTitle id="simple-dialog-title">
        {props.isValidation
          ? translate({ ...messages.RegulationValidation }, props)
          : translate({ ...messages.RegulationRejection }, props)}
      </DialogTitle>
      <DialogContent>
        <StyledDiv>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={8}>
              {!regulationValidationComment &&
                !props.isValidation &&
                isNil(reasonCode) &&
                getElementTitle(
                  get(props.listings, 'regulationRejectionReasons', []),
                  reasonCode,
                ) === OTHER_REJECTION_REASON && (
                  <FormHelperText error>
                    {translate({ ...messages.rejectReason }, props)}
                  </FormHelperText>
                )}
              {!props.isValidation && (
                <FormControl className="select">
                  <InputLabel
                    ref={inputLabel}
                    id="demo-simple-select-outlined-label"
                  >
                    {'Type'}
                  </InputLabel>
                  <Select
                    name={'type'}
                    value={rejectionType}
                    onChange={handleChangeRejectionType}
                    labelWidth={100}
                  >
                    {REJECTION_REGULATION_TYPE?.map((val: any) => (
                      <MenuItem key={uniqueId('type_')} value={val.type}>
                        {val.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {!props.isValidation && !isNil(rejectionType) && (
                <FormControl className="select">
                  <InputLabel
                    ref={inputLabel}
                    id="demo-simple-select-outlined-label"
                  >
                    {'Motif'}
                  </InputLabel>
                  <Select
                    name={'motif'}
                    value={reasonCode}
                    onChange={handleChangeReason}
                    labelWidth={100}
                  >
                    {!isEmpty(
                      get(props.listings, 'regulationRejectionReasons', []),
                    ) &&
                      get(props.listings, 'regulationRejectionReasons', [])
                        .filter(
                          (el: any) =>
                            el.type === rejectionType || isNil(el.type),
                        )
                        ?.map((val: any) => (
                          <MenuItem key={uniqueId('status_')} value={val.id}>
                            {val.title}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              )}
              {(props.isValidation ||
                featureService.hasFeature(FEATURES.EVOLUTION_P1) ||
                (!props.isValidation &&
                  getElementTitle(
                    get(props.listings, 'regulationRejectionReasons', []),
                    reasonCode,
                  ) === OTHER_REJECTION_REASON &&
                  !isNil(rejectionType))) && (
                <TextField
                  className="select"
                  label={textFieldLabel}
                  name={'RegulationValidation'}
                  id={'RegulationValidation'}
                  value={regulationValidationComment}
                  onChange={handleChange}
                  fullWidth
                  error={
                    !regulationValidationComment &&
                    !props.isValidation &&
                    getElementTitle(
                      get(props.listings, 'regulationRejectionReasons', []),
                      reasonCode,
                    ) === OTHER_REJECTION_REASON
                  }
                />
              )}
            </Grid>
          </Grid>
        </StyledDiv>
      </DialogContent>
      <StyledDialogActions>
        <SecondaryButton
          onClick={handleClose}
          disableElevation
          title={translate(
            { ...messages.RegulationVaidationDialogCancel },
            props,
          )}
        />
        <PrimaryButton
          onClick={props.handleValidation(
            regulationValidationComment,
            reasonCode,
            rejectionType,
          )}
          title={translate(
            { ...messages.validationRegulationDrawerButtonTitle },
            props,
          )}
          disabled={disableButton}
        />
      </StyledDialogActions>
    </Dialog>
  );
};

RegulationValidationDialog.defaultProps = {
  open: false,
};

export default injectIntl(RegulationValidationDialog);
